import React, { useMemo, useState } from 'react'
import { Button } from '@reapit/elements/core/button'
import { toast } from '@reapit/elements/core/toaster'
import { CopyIcon } from '@reapit/elements/icons/copy'
import { AdminActionTemplate, AdminEventTemplate, FullEventTemplate } from '../../../../api/types'
import { getEnumLabel, useAdminEnums } from '../../../../api/enums'
import { useBuilderDeletes } from '../../../../api/builder-deletes'
import { EventTemplateDrawer } from '../event-template-drawer'
import { ActionTemplateDrawer } from '../action-template-drawer'
import { CopyTemplatesDrawer } from '../copy-templates-drawer'
import { ConfirmDeleteDialog } from '../../../../components/confirm-delete-dialog'
import { useAccessMode } from '../../../../access-mode-provider'
import {
  BuilderSplitLayout,
  Canvas,
  CardTile,
  EmptyState,
  LeftColumn,
  OutlineActions,
  OutlineAddLink,
  OutlineCard,
  OutlineCardName,
  OutlineTree,
  SideCardHelp,
} from '../../style'
import {
  ActionName,
  ActionRow,
  CopyNote,
  CopySection,
  GroupCaption,
  NeutralPill,
  OutlineActionsStack,
  Pill,
  Pills,
  Preview,
  RowActions,
  SectionHeading,
  SectionHint,
  SectionTitle,
  TemplateRow,
  TemplatesPanel,
} from './style'

interface NotificationsActionsTabProps {
  formReferenceID: string
  versionReferenceID: string
  eventTemplates: FullEventTemplate[]
  actionTemplates: AdminActionTemplate[]
  /** The form's document family ("Agreement" | "Notice") — narrows the Activity choices offered when
   * adding a notification, since most activities can never fire for a Notice. */
  family?: string
  onChanged: () => void
}

const scrollToSection = (id: string) =>
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })

/**
 * The Notifications & Actions builder tab. Notifications (event templates) and audit-log actions are
 * scoped to this specific form version — editing them here never affects any other version of the form.
 */
export const NotificationsActionsTab: React.FC<NotificationsActionsTabProps> = ({
  formReferenceID,
  versionReferenceID,
  eventTemplates,
  actionTemplates,
  family,
  onChanged,
}) => {
  const { data: enums } = useAdminEnums()
  const { deleteEventTemplate, deleteActionTemplate, deleteAllEventTemplates, deleteAllActionTemplates } =
    useBuilderDeletes()
  const { isReadOnly } = useAccessMode()

  const [isEventDrawerOpen, setIsEventDrawerOpen] = useState(false)
  const [editingEvent, setEditingEvent] = useState<AdminEventTemplate | null>(null)
  const [removingEvent, setRemovingEvent] = useState<AdminEventTemplate | null>(null)
  const [removingAllEvents, setRemovingAllEvents] = useState(false)

  const [isActionDrawerOpen, setIsActionDrawerOpen] = useState(false)
  const [editingAction, setEditingAction] = useState<AdminActionTemplate | null>(null)
  const [removingAction, setRemovingAction] = useState<AdminActionTemplate | null>(null)
  const [removingAllActions, setRemovingAllActions] = useState(false)

  const [deleting, setDeleting] = useState(false)
  const [isCopyDrawerOpen, setIsCopyDrawerOpen] = useState(false)

  // Copy only bootstraps an empty form — the backend rejects it otherwise (see BE-M4-3), so gate the
  // action here too rather than letting the admin open the drawer just to hit a 409.
  const canCopy = eventTemplates.length === 0 && actionTemplates.length === 0

  const openCreateEvent = () => {
    setEditingEvent(null)
    setIsEventDrawerOpen(true)
  }
  const openEditEvent = (template: AdminEventTemplate) => {
    setEditingEvent(template)
    setIsEventDrawerOpen(true)
  }
  const openCreateAction = () => {
    setEditingAction(null)
    setIsActionDrawerOpen(true)
  }
  const openEditAction = (template: AdminActionTemplate) => {
    setEditingAction(template)
    setIsActionDrawerOpen(true)
  }

  // Group notifications under their triggering activity, preserving first-seen (ID) order of both the
  // activities and the templates within each.
  const groups = useMemo(() => {
    const byActivity = new Map<string, AdminEventTemplate[]>()
    for (const entry of eventTemplates) {
      const list = byActivity.get(entry.eventTemplate.activity) ?? []
      list.push(entry.eventTemplate)
      byActivity.set(entry.eventTemplate.activity, list)
    }
    return Array.from(byActivity.entries())
  }, [eventTemplates])

  const confirmRemoveEvent = async () => {
    if (!removingEvent) return
    setDeleting(true)
    try {
      await deleteEventTemplate(formReferenceID, versionReferenceID, removingEvent.referenceID)
      setRemovingEvent(null)
      onChanged()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not remove this notification.')
    } finally {
      setDeleting(false)
    }
  }

  const confirmRemoveAction = async () => {
    if (!removingAction) return
    setDeleting(true)
    try {
      await deleteActionTemplate(formReferenceID, versionReferenceID, removingAction.referenceID)
      setRemovingAction(null)
      onChanged()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not remove this action.')
    } finally {
      setDeleting(false)
    }
  }

  const confirmRemoveAllEvents = async () => {
    setDeleting(true)
    try {
      await deleteAllEventTemplates(formReferenceID, versionReferenceID)
      setRemovingAllEvents(false)
      onChanged()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not remove the notifications.')
    } finally {
      setDeleting(false)
    }
  }

  const confirmRemoveAllActions = async () => {
    setDeleting(true)
    try {
      await deleteAllActionTemplates(formReferenceID, versionReferenceID)
      setRemovingAllActions(false)
      onChanged()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not remove the actions.')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <BuilderSplitLayout>
      <LeftColumn>
        <CardTile>
          <SideCardHelp>These apply only to this version — other versions keep their own set.</SideCardHelp>
          <SideCardHelp>
            If agencies have reminders set up for this form, make sure this version has the same notifications
            configured. A new version with no matching notification silently stops those reminders from firing —
            there&apos;s no warning to the agency when that happens.
          </SideCardHelp>
          <OutlineTree>
            <OutlineCard>
              <OutlineCardName
                variant="tertiary"
                useLinkStyle
                type="button"
                onClick={() => scrollToSection('notifications-section')}
              >
                Notifications ({eventTemplates.length})
              </OutlineCardName>
              {!isReadOnly && (
                <OutlineActions>
                  <OutlineActionsStack>
                    <OutlineAddLink variant="tertiary" useLinkStyle type="button" onClick={openCreateEvent}>
                      + Add notification
                    </OutlineAddLink>
                    {eventTemplates.length > 0 && (
                      <OutlineAddLink
                        variant="tertiary"
                        useLinkStyle
                        isDestructive
                        type="button"
                        onClick={() => setRemovingAllEvents(true)}
                      >
                        − Delete all notifications
                      </OutlineAddLink>
                    )}
                  </OutlineActionsStack>
                </OutlineActions>
              )}
            </OutlineCard>
            <OutlineCard>
              <OutlineCardName
                variant="tertiary"
                useLinkStyle
                type="button"
                onClick={() => scrollToSection('actions-section')}
              >
                Actions ({actionTemplates.length})
              </OutlineCardName>
              {!isReadOnly && (
                <OutlineActions>
                  <OutlineActionsStack>
                    <OutlineAddLink variant="tertiary" useLinkStyle type="button" onClick={openCreateAction}>
                      + Add action
                    </OutlineAddLink>
                    {actionTemplates.length > 0 && (
                      <OutlineAddLink
                        variant="tertiary"
                        useLinkStyle
                        isDestructive
                        type="button"
                        onClick={() => setRemovingAllActions(true)}
                      >
                        − Delete all actions
                      </OutlineAddLink>
                    )}
                  </OutlineActionsStack>
                </OutlineActions>
              )}
            </OutlineCard>
          </OutlineTree>

          {!isReadOnly && (
            <CopySection>
              <CopyNote>
                {canCopy
                  ? "Clone another form's current version set, then edit. Only when this version has none."
                  : 'Only available while this version has no notifications or actions.'}
              </CopyNote>
              <Button
                variant="primary"
                size="medium"
                iconLeft={<CopyIcon />}
                disabled={!canCopy}
                onClick={() => setIsCopyDrawerOpen(true)}
                type="button"
                style={{ marginTop: '0.75rem' }}
              >
                Copy from another form
              </Button>
            </CopySection>
          )}
        </CardTile>
      </LeftColumn>

      <Canvas>
        <div id="notifications-section">
          <SectionHeading>
            <SectionTitle>Notifications</SectionTitle>
            <SectionHint>— sent when a document event occurs</SectionHint>
          </SectionHeading>

          {eventTemplates.length === 0 ? (
            <EmptyState>No notifications yet. Add one, or copy a similar form&apos;s set.</EmptyState>
          ) : (
            <TemplatesPanel>
              {groups.map(([activity, templates]) => (
                <React.Fragment key={activity}>
                  <GroupCaption>{getEnumLabel(enums, 'DocumentActivity', activity) || activity}</GroupCaption>
                  {templates.map((template) => {
                    const methodLabel = getEnumLabel(enums, 'CommunicationType', template.method) || template.method
                    const entityLabel = getEnumLabel(enums, 'PersonType', template.entityType) || template.entityType
                    const preview = template.method === 'Email' ? template.subject : template.body
                    return (
                      <TemplateRow key={template.referenceID}>
                        <Pills>
                          <Pill>{methodLabel}</Pill>
                          {template.entityType && template.entityType !== 'None' && (
                            <NeutralPill>{entityLabel}</NeutralPill>
                          )}
                        </Pills>
                        <Preview title={preview}>{preview}</Preview>
                        {!isReadOnly && (
                          <RowActions>
                            <Button
                              variant="tertiary"
                              useLinkStyle
                              size="small"
                              type="button"
                              onClick={() => openEditEvent(template)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="tertiary"
                              useLinkStyle
                              isDestructive
                              size="small"
                              type="button"
                              onClick={() => setRemovingEvent(template)}
                            >
                              Delete
                            </Button>
                          </RowActions>
                        )}
                      </TemplateRow>
                    )
                  })}
                </React.Fragment>
              ))}
            </TemplatesPanel>
          )}
        </div>

        <div id="actions-section">
          <SectionHeading>
            <SectionTitle>Actions</SectionTitle>
            <SectionHint>— activity log line recorded for each event</SectionHint>
          </SectionHeading>

          {actionTemplates.length === 0 ? (
            <EmptyState>No actions yet. Add one, or copy a similar form&apos;s set.</EmptyState>
          ) : (
            <TemplatesPanel>
              {actionTemplates.map((template) => (
                <ActionRow key={template.referenceID}>
                  <ActionName>{getEnumLabel(enums, 'DocumentActivity', template.action) || template.action}</ActionName>
                  <Preview title={template.description}>{template.description}</Preview>
                  {!isReadOnly && (
                    <RowActions>
                      <Button
                        variant="tertiary"
                        useLinkStyle
                        size="small"
                        type="button"
                        onClick={() => openEditAction(template)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="tertiary"
                        useLinkStyle
                        isDestructive
                        size="small"
                        type="button"
                        onClick={() => setRemovingAction(template)}
                      >
                        Delete
                      </Button>
                    </RowActions>
                  )}
                </ActionRow>
              ))}
            </TemplatesPanel>
          )}
        </div>
      </Canvas>

      <EventTemplateDrawer
        isOpen={isEventDrawerOpen}
        onOpenChange={setIsEventDrawerOpen}
        formReferenceID={formReferenceID}
        versionReferenceID={versionReferenceID}
        eventTemplate={editingEvent}
        family={family}
        onSaved={onChanged}
      />
      <ActionTemplateDrawer
        isOpen={isActionDrawerOpen}
        onOpenChange={setIsActionDrawerOpen}
        formReferenceID={formReferenceID}
        versionReferenceID={versionReferenceID}
        actionTemplate={editingAction}
        family={family}
        onSaved={onChanged}
      />
      <ConfirmDeleteDialog
        isOpen={Boolean(removingEvent)}
        title="Delete notification"
        message="Delete this notification? This can't be undone."
        busy={deleting}
        onConfirm={confirmRemoveEvent}
        onCancel={() => setRemovingEvent(null)}
      />
      <ConfirmDeleteDialog
        isOpen={Boolean(removingAction)}
        title="Delete action"
        message="Delete this action? This can't be undone."
        busy={deleting}
        onConfirm={confirmRemoveAction}
        onCancel={() => setRemovingAction(null)}
      />
      <ConfirmDeleteDialog
        isOpen={removingAllEvents}
        title="Delete all notifications"
        message={`Delete all ${eventTemplates.length} notification(s) on this version? This can't be undone.`}
        busy={deleting}
        onConfirm={confirmRemoveAllEvents}
        onCancel={() => setRemovingAllEvents(false)}
      />
      <ConfirmDeleteDialog
        isOpen={removingAllActions}
        title="Delete all actions"
        message={`Delete all ${actionTemplates.length} action(s) on this version? This can't be undone.`}
        busy={deleting}
        onConfirm={confirmRemoveAllActions}
        onCancel={() => setRemovingAllActions(false)}
      />
      <CopyTemplatesDrawer
        isOpen={isCopyDrawerOpen}
        onOpenChange={setIsCopyDrawerOpen}
        formReferenceID={formReferenceID}
        versionReferenceID={versionReferenceID}
        family={family}
        onCopied={onChanged}
      />
    </BuilderSplitLayout>
  )
}
